import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, StatusBadge, formatCurrency, formatDate } from "@/components/shared";
import { ArrowLeft, DollarSign, FileDown } from "lucide-react";
import { toast } from "sonner";

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [pay, setPay] = useState({ amount: 0, method: "Check", check_no: "", notes: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
      const [t, p] = await Promise.all([
        api.get(`/tenants/${data.tenant_id}`),
        api.get(`/payments?tenant_id=${data.tenant_id}`),
      ]);
      setTenant(t.data);
      setPayments(p.data.filter((x) => x.invoice_id === id));
      setPay({ amount: data.balance, method: "Check", check_no: "", notes: "" });
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, [id]);

  const recordPayment = async () => {
    const amt = parseFloat(pay.amount);
    if (!amt || amt <= 0) { toast.error("Enter a payment amount"); return; }
    try {
      await api.post(`/invoices/${id}/payments`, { ...pay, amount: amt, invoice_id: id });
      toast.success("Payment recorded");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  if (!inv) return <div className="p-8 text-sm text-[#737A76]">Loading…</div>;

  return (
    <div data-testid="invoice-detail-page">
      <Button variant="ghost" onClick={() => navigate("/invoices")} className="-ml-2 mb-2"><ArrowLeft size={14} className="mr-1" /> Invoices</Button>
      <PageHeader
        title={inv.invoice_no}
        subtitle={<span className="flex items-center gap-2">{tenant ? `${tenant.first_name} ${tenant.last_name}` : ""} <StatusBadge status={inv.status} /></span>}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-[#EAEAE5]" onClick={() => window.open(`${API}/invoices/${id}/pdf`, "_blank")} data-testid="download-invoice-pdf">
              <FileDown size={14} className="mr-2" /> PDF
            </Button>
            {inv.balance > 0 && (
              <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="record-payment-button"><DollarSign size={14} className="mr-2" /> Record Payment</Button>
            </DialogTrigger>
            <DialogContent className="!bg-white border-[#EAEAE5]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Outfit" }}>Record Payment</DialogTitle>
                <DialogDescription className="text-xs text-[#737A76]">Record a payment against this invoice.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Amount</Label><Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount-input" /></div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#737A76]">Method</Label>
                  <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Check">Check</SelectItem>
                      <SelectItem value="Credit Card">Credit Card</SelectItem>
                      <SelectItem value="ACH">ACH</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {pay.method === "Check" && (
                  <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Check #</Label><Input value={pay.check_no} onChange={(e) => setPay({ ...pay, check_no: e.target.value })} /></div>
                )}
                <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Notes</Label><Input value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={recordPayment} data-testid="submit-payment-button">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-[#EAEAE5] shadow-none lg:col-span-2"><CardContent className="p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Items</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#737A76] border-b border-[#EAEAE5]"><th className="py-2">Description</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {(inv.items || []).map((it, i) => (
                <tr key={i} className="border-b border-[#F0F0EC] last:border-0"><td className="py-2.5">{it.description}</td><td className="text-right">{formatCurrency(it.amount)}</td></tr>
              ))}
            </tbody>
          </table>

          <div className="text-[10px] uppercase tracking-widest text-[#737A76] pt-4">Payments</div>
          {payments.length === 0 ? (
            <div className="text-sm text-[#737A76]">No payments recorded.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#737A76] border-b border-[#EAEAE5]"><th className="py-2">Date</th><th>Method</th><th>Check #</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-[#F0F0EC] last:border-0">
                    <td className="py-2.5">{formatDate(p.date)}</td>
                    <td>{p.method}</td>
                    <td className="text-[#737A76]">{p.check_no || "—"}</td>
                    <td className="text-right">{formatCurrency(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent></Card>

        <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-2.5">
          <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Summary</div>
          <Row label="Subtotal" value={formatCurrency(inv.subtotal)} />
          <Row label="Tax" value={formatCurrency(inv.tax)} />
          <Row label="Total" value={formatCurrency(inv.total)} bold />
          <Row label="Paid" value={formatCurrency(inv.paid)} />
          <div className="border-t border-[#EAEAE5] my-2" />
          <Row label="Balance" value={formatCurrency(inv.balance)} bold large />
        </CardContent></Card>
      </div>
    </div>
  );
}

function Row({ label, value, bold, large }) {
  return (
    <div className={`flex justify-between ${large ? "text-base" : "text-sm"}`}>
      <span className="text-[#737A76]">{label}</span>
      <span className={bold ? "font-semibold" : ""}>{value}</span>
    </div>
  );
}
